import React from 'react';
import { useStore } from '../../store';
import { getSymbolContext } from '../../selectors/symbolContext';
import { CSVImport } from '../chart/CSVImport';
import { useGammaSnapshot } from './useGammaSnapshot';
import { resolveGammaUiState } from './gammaUiState';
import { LIVE_GAMMA_UI_ENABLED } from './gammaUiConfig';

// ── Live Gamma Card (server-side data via theta-gateway) ──────────────────────────────
// Displays real-time gamma snapshot computed server-side from ThetaData chain state.
// Renders independently of the legacy CSV-based OI panel below it.
// Gracefully absent when data is unavailable — never blocks dashboard rendering.

const REGIME_COLOR: Record<string, string> = {
  POSITIVE: 'var(--green)',
  NEGATIVE: 'var(--red)',
  NEUTRAL:  'var(--amber)',
};
const BIAS_LABEL: Record<string, string> = {
  PIN:                'Pin / Compression',
  UPSIDE_EXPANSION:   'Upside Expansion',
  DOWNSIDE_EXPANSION: 'Downside Expansion',
  BALANCED:           'Balanced',
};

function fmt(n: number | null | undefined, decimals = 2): string {
  if (n === null || n === undefined) return '—';
  return n.toFixed(decimals);
}

function ScoreBar({ value, max = 100, color }: { value: number | null; max?: number; color: string }) {
  if (value === null || value === undefined) return <span style={{ color: 'var(--t3)', fontSize: 11 }}>—</span>;
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span className="mono" style={{ fontSize: 11, color: 'var(--t1)', minWidth: 28, textAlign: 'right' }}>{Math.round(value)}</span>
    </div>
  );
}

function LiveGammaCard({ symbol }: { symbol: string }) {
  // LIVE_GAMMA_UI_ENABLED gate — no fetch when disabled
  const { loading, snapshot, error, refresh } = useGammaSnapshot(
    LIVE_GAMMA_UI_ENABLED ? symbol : '',
    { autoRefreshMs: LIVE_GAMMA_UI_ENABLED ? 20_000 : 0 }
  );

  if (!LIVE_GAMMA_UI_ENABLED) return null;

  const uiState = resolveGammaUiState(loading, snapshot, error);

  const headerRow = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--t0)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
          Live Gamma
        </span>
        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--purple)', background: 'var(--purple-bg)', padding: '1px 5px', borderRadius: 3, border: '1px solid var(--purple)35', fontFamily: 'var(--mono)' }}>
          6.1
        </span>
      </div>
      <button
        onClick={refresh}
        disabled={loading}
        title="Refresh live gamma"
        style={{ background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', color: loading ? 'var(--t3)' : 'var(--t2)', fontSize: 12, padding: '2px 4px' }}
      >
        {loading ? '⏳' : '↻'}
      </button>
    </div>
  );

  // Loading or unavailable — compact single-line card
  if (!uiState.showContent) {
    return (
      <div style={{
        padding: '10px 12px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 12,
        border: `1px solid ${uiState.borderColor}`,
        opacity: uiState.status === 'unavailable' ? 0.75 : 1,
      }}>
        {headerRow}
        <div style={{ fontSize: 11, color: 'var(--t2)' }}>{uiState.statusLabel}</div>
      </div>
    );
  }

  // Full content — snapshot is available (fresh or stale)
  const s          = uiState.snapshot!;
  const regColor   = s.regime ? (REGIME_COLOR[s.regime] ?? 'var(--t2)') : 'var(--t2)';
  const biasLabel  = s.bias   ? (BIAS_LABEL[s.bias]    ?? s.bias)       : '—';
  const staleSec   = s.computedAt ? Math.round((Date.now() - s.computedAt) / 1000) : null;

  return (
    <div style={{
      padding: '10px 12px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 12,
      border: `1px solid ${uiState.borderColor}`,
    }}>
      {headerRow}

      {/* Stale banner — rendered when uiState resolved to 'stale' */}
      {uiState.status === 'stale' && (
        <div style={{ padding: '4px 8px', background: 'var(--amber-bg)', border: '1px solid var(--amber-bd)', borderRadius: 5, marginBottom: 8, fontSize: 11, color: 'var(--amber)' }}>
          {uiState.statusLabel}
        </div>
      )}

      {/* Regime + Bias */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 120, padding: '6px 10px', background: 'var(--bg4)', borderRadius: 6, borderLeft: `3px solid ${regColor}` }}>
          <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Regime</div>
          <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: regColor }}>{s.regime ?? '—'}</div>
        </div>
        <div style={{ flex: 1, minWidth: 120, padding: '6px 10px', background: 'var(--bg4)', borderRadius: 6 }}>
          <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Bias</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>{biasLabel}</div>
        </div>
      </div>

      {/* Scores */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'grid', gap: 5 }}>
          {[
            { label: 'Pin Score',       value: s.pinScore,       color: 'var(--cyan)' },
            { label: 'Expansion Score', value: s.expansionScore, color: 'var(--purple)' },
            { label: 'Gamma Score',     value: s.gammaScore,     color: 'var(--gold)' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, color: 'var(--t3)' }}>{label}</span>
              <ScoreBar value={value} color={color} />
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--t3)' }}>Directional</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 4, background: 'var(--bg4)', borderRadius: 2, position: 'relative' }}>
                {/* Centered bar — directional is -100 to +100 */}
                {s.directionalScore !== null && (
                  <div style={{
                    position: 'absolute',
                    left:   s.directionalScore >= 0 ? '50%' : `${50 + s.directionalScore / 2}%`,
                    width:  `${Math.abs(s.directionalScore ?? 0) / 2}%`,
                    height: '100%',
                    background: (s.directionalScore ?? 0) >= 0 ? 'var(--green)' : 'var(--red)',
                    borderRadius: 2,
                  }} />
                )}
              </div>
              <span className="mono" style={{ fontSize: 11, color: (s.directionalScore ?? 0) >= 0 ? 'var(--green)' : 'var(--red)', minWidth: 32, textAlign: 'right' }}>
                {s.directionalScore !== null ? (s.directionalScore >= 0 ? '+' : '') + Math.round(s.directionalScore) : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Key strikes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 8 }}>
        {[
          { label: 'Call Wall',  value: s.callWall,        color: 'var(--green)' },
          { label: 'Put Wall',   value: s.putWall,         color: 'var(--red)'   },
          { label: 'Magnet',     value: s.keyMagnetStrike, color: 'var(--amber)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ padding: '5px 8px', background: 'var(--bg4)', borderRadius: 5 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div className="mono" style={{ fontSize: 13, fontWeight: 600, color }}>{fmt(value)}</div>
          </div>
        ))}
      </div>

      {/* Flip zone + breakout fuel (compact) */}
      {(s.flipZoneLow !== null || s.breakoutFuelUp !== null) && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {s.flipZoneLow !== null && s.flipZoneHigh !== null && (
            <div style={{ padding: '4px 8px', background: 'var(--bg4)', borderRadius: 5 }}>
              <span style={{ fontSize: 9, color: 'var(--t3)' }}>Flip Zone: </span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--cyan)' }}>{fmt(s.flipZoneLow)}–{fmt(s.flipZoneHigh)}</span>
            </div>
          )}
          {s.breakoutFuelUp !== null && (
            <div style={{ padding: '4px 8px', background: 'var(--bg4)', borderRadius: 5 }}>
              <span style={{ fontSize: 9, color: 'var(--t3)' }}>Fuel ↑ </span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--green)' }}>{(s.breakoutFuelUp * 100).toFixed(0)}%</span>
              <span style={{ fontSize: 9, color: 'var(--t3)' }}> ↓ </span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--red)' }}>{((s.breakoutFuelDown ?? 0) * 100).toFixed(0)}%</span>
            </div>
          )}
        </div>
      )}

      {/* Trade behavior note */}
      {s.tradeBehaviorProfile?.note && (
        <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.5, padding: '5px 8px', background: 'var(--bg4)', borderRadius: 5, marginBottom: 6 }}>
          {s.tradeBehaviorProfile.note}
        </div>
      )}

      {/* Engine notes */}
      {s.notes && s.notes.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {s.notes.slice(0, 3).map((n, i) => (
            <div key={i} style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 2 }}>• {n}</div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 6, fontSize: 9, color: 'var(--t3)', display: 'flex', gap: 8 }}>
        <span>Quality: {s.dataQuality ?? '—'}</span>
        {staleSec !== null && <span>Age: {staleSec}s</span>}
        {s.computedAt && <span>Computed: {new Date(s.computedAt).toLocaleTimeString()}</span>}
      </div>

      <div style={{ marginTop: 4, fontSize: 9, color: 'var(--t3)', fontStyle: 'italic' }}>
        Proxy-based gamma estimate — not dealer-exact GEX. Observational only.
      </div>
    </div>
  );
}

function OIBarChart({ options, curPrice, gammaFlip }: { options: any[]; curPrice?: number; gammaFlip?: number | null }) {
  if (!options.length) return null;
  const W = 740, H = 160, PAD = { l: 44, r: 10, t: 8, b: 24 };
  const cW = W - PAD.l - PAD.r, cH = H - PAD.t - PAD.b;
  const maxOI = Math.max(...options.map(o => Math.max(o.callOI, o.putOI)));
  const bW = (cW / options.length) * 0.36;
  const xOf = (i: number) => PAD.l + (i + 0.5) * (cW / options.length);
  const yOf = (v: number) => PAD.t + cH - (v / maxOI) * cH;
  const minS = Math.min(...options.map(o => o.strike));
  const maxS = Math.max(...options.map(o => o.strike));
  const priceToX = (p: number) => PAD.l + ((p - minS) / (maxS - minS)) * cW;

  return (
    <div>
      <div className="sl">OI Distribution</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%' }}>
        <rect width={W} height={H} fill="transparent" />
        {[0.25, 0.5, 0.75, 1].map(f => (
          <line key={f} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + cH * (1 - f)} y2={PAD.t + cH * (1 - f)} stroke="rgba(255,255,255,.04)" strokeWidth={1} />
        ))}
        {options.map((o, i) => {
          const x = xOf(i);
          return (
            <g key={i}>
              <rect x={x} y={yOf(o.callOI)} width={bW} height={Math.max(1, PAD.t + cH - yOf(o.callOI))} fill="rgba(15,232,137,.45)" rx={1} />
              <rect x={x - bW} y={yOf(o.putOI)} width={bW} height={Math.max(1, PAD.t + cH - yOf(o.putOI))} fill="rgba(255,62,94,.4)" rx={1} />
              <text x={x} y={H - 6} textAnchor="middle" fill="#384858" fontSize={7} fontFamily="JetBrains Mono">{o.strike}</text>
            </g>
          );
        })}
        {curPrice !== undefined && curPrice >= minS && curPrice <= maxS && (
          <line x1={priceToX(curPrice)} x2={priceToX(curPrice)} y1={PAD.t} y2={PAD.t + cH} stroke="rgba(45,156,219,.8)" strokeWidth={1.5} strokeDasharray="3,2" />
        )}
        {gammaFlip !== null && gammaFlip !== undefined && gammaFlip >= minS && gammaFlip <= maxS && (
          <line x1={priceToX(gammaFlip!)} x2={priceToX(gammaFlip!)} y1={PAD.t} y2={PAD.t + cH} stroke="rgba(255,176,32,.7)" strokeWidth={1} strokeDasharray="2,2" />
        )}
      </svg>
      <div className="flex gap10 mt4" style={{ fontSize: 11, color: 'var(--t2)', justifyContent: 'center' }}>
        <span>■ <span style={{ color: 'var(--green)' }}>Call OI</span></span>
        <span>■ <span style={{ color: 'var(--red)' }}>Put OI</span></span>
        {curPrice !== undefined && <span>│ <span style={{ color: 'var(--blue)' }}>Price</span></span>}
        {gammaFlip && <span>│ <span style={{ color: 'var(--amber)' }}>Gamma Flip</span></span>}
      </div>
    </div>
  );
}

export function GammaPanel({ symbol }: { symbol: string }) {
  const { symbolData, settings, weights, setOptions } = useStore();
  const data = symbolData[symbol];
  const gamma = getSymbolContext(symbol, data, settings, weights)?.gamma ?? null;
  const regColor = gamma ? (gamma.regime.includes('Long') ? 'var(--green)' : gamma.regime.includes('Short') ? 'var(--red)' : 'var(--amber)') : 'var(--t2)';
  const volRiskColor = { Low: 'var(--green)', Medium: 'var(--amber)', High: 'var(--red)' };

  const loadSampleOptions = () => {
    const curPrice = data?.candles?.length ? data.candles[data.candles.length - 1].close : 100;
    const mag = curPrice < 20 ? 1 : curPrice < 100 ? 5 : curPrice < 500 ? 10 : 50;
    const base = Math.round(curPrice / mag) * mag;
    const rows = Array.from({ length: 13 }, (_, i) => {
      const strike = base + (i - 6) * mag;
      if (strike <= 0) return null;
      const dist = Math.abs(i - 6);
      const callBias = i > 6 ? 1.5 : 0.6;
      const putBias  = i < 6 ? 1.5 : 0.6;
      return {
        strike,
        callOI:  Math.round((30000 - dist * 2500) * callBias + Math.random() * 3000),
        putOI:   Math.round((30000 - dist * 2500) * putBias  + Math.random() * 3000),
        gamma: null, callVol: 0, putVol: 0, expiration: null
      };
    }).filter(Boolean) as any[];
    setOptions(symbol, rows);
  };

  return (
    <div className="card">
      <div className="fsb mb12">
        <div className="sl" style={{ marginBottom: 0 }}>Gamma / Options Regime</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!data?.options?.length && (
            <button className="btn btn-sm" onClick={loadSampleOptions} title="Load simulated OI data to preview the gamma panel">
              ⚡ Load Sample OI
            </button>
          )}
          {data?.options?.length && (
            <button className="btn btn-sm" onClick={() => setOptions(symbol, [])} title="Clear options data">
              ✕ Clear OI
            </button>
          )}
          {/* TODO: legacy CSV-based OI import predates the live ThetaData gamma path.
              Now that LiveGammaCard supplies real-time options chain context, this
              upload UI is redundant for testers. Audit and likely remove (Phase 4 cleanup). */}
          <CSVImport type="options" symbol={symbol} hasData={!!(data?.options?.length)} />
        </div>
      </div>

      {/* Live gamma via theta-gateway (ThetaData chain state) — always shown, degrades gracefully */}
      <LiveGammaCard symbol={symbol} />

      {!gamma ? (
        <div>
          <div style={{ padding: '12px 14px', background: 'var(--amber-bg)', border: '1px solid var(--amber-bd)', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--amber)', marginBottom: 6 }}>⚡ Options OI Data Required</div>
            <div style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.7, marginBottom: 8 }}>
              Gamma analysis needs options open interest data — specifically which strikes have the most call and put contracts open.
              This is <strong>not included in TwelveData or other OHLCV APIs</strong>. You need to import it separately as a CSV.
            </div>
            <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.6 }}>
              The regime engine is still running but with <span style={{ color: 'var(--amber)' }}>reduced confidence</span> — all gamma-related signals (pin zones, call/put walls, gamma flip) will be unavailable until you import options data.
            </div>
          </div>

          <div style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t0)', marginBottom: 10 }}>Where to get options OI data (free)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { source: 'Barchart.com', steps: `Search ${symbol} → Options → Download (CSV button top-right). Use "OI" column.`, color: 'var(--blue)' },
                { source: 'Market Chameleon', steps: `Search ${symbol} → Options Chain → Export CSV. Has call_oi and put_oi columns.`, color: 'var(--green)' },
                { source: 'CBOE (cboe.com)', steps: 'Delayed options data, free download. Search symbol → Options Chain → Export.', color: 'var(--purple)' },
                { source: 'TD Ameritrade / Schwab', steps: 'Options chain → right-click → Export to CSV. Best data quality.', color: 'var(--cyan)' },
              ].map(({ source, steps, color }) => (
                <div key={source} style={{ display: 'flex', gap: 10, padding: '8px 10px', background: 'var(--bg4)', borderRadius: 6, borderLeft: `3px solid ${color}` }}>
                  <div style={{ minWidth: 140, fontSize: 12, fontWeight: 700, color }}>{source}</div>
                  <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.5 }}>{steps}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t0)', marginBottom: 8 }}>Required CSV format</div>
            <div style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 6 }}>
              The file needs at minimum these three columns (column names are flexible — the parser auto-detects common variants):
            </div>
            <pre style={{ fontSize: 12, color: 'var(--cyan)', background: 'var(--bg0)', padding: '10px 14px', borderRadius: 6, lineHeight: 1.8, overflowX: 'auto' }}>
{`strike,call_oi,put_oi
100,8500,22000
105,12000,18500
110,28000,9000
115,15000,5500
120,9000,3200`}
            </pre>
            <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 6 }}>
              Optional columns also accepted: <span style={{ fontFamily: 'var(--mono)', color: 'var(--t2)' }}>gamma_estimate, call_volume, put_volume, expiration</span>
            </div>
          </div>

          <div style={{ padding: '14px', background: 'var(--bg3)', borderRadius: 8, border: '2px dashed var(--blue-bd)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t0)', marginBottom: 8, textAlign: 'center' }}>
              Drop your options CSV here or click to browse
            </div>
            {/* TODO: see comment above first CSVImport � same redundancy concern (Phase 4 cleanup). */}
            <CSVImport type="options" symbol={symbol} hasData={false} />
          </div>

          <div className="disclaimer" style={{ marginTop: 10 }}>
            Options data is inferred from OI structure only — not exact dealer positioning. All gamma outputs are probabilistic estimates.
          </div>
        </div>
      ) : (
        <>
          <div className="card-inner mb12" style={{ borderLeft: `3px solid ${regColor}` }}>
            <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 3 }}>INFERRED REGIME</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: regColor }}>{gamma.regime}</div>
            <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 3 }}>
              Vol expansion risk: <span style={{ color: (volRiskColor as any)[gamma.volRisk], fontWeight: 600 }}>{gamma.volRisk}</span>
              &nbsp;·&nbsp; Data confidence: <span style={{ color: 'var(--t1)', fontWeight: 600 }}>{gamma.confidence}</span>
            </div>
          </div>
          <div className="g3" style={{ gap: 6, marginBottom: 12 }}>
            {[
              { l: 'Gamma Flip', v: gamma.gammaFlip?.toFixed(2) || '—', c: 'var(--amber)' },
              { l: 'Max Pain', v: gamma.maxPain?.toFixed(2) || '—', c: 'var(--cyan)' },
              { l: 'Pin Strike', v: gamma.pinStrike?.toFixed(2) || '—', c: 'var(--purple)' },
              { l: 'Call Wall', v: gamma.callWall?.toFixed(2) || '—', c: 'var(--green)' },
              { l: 'Put Wall', v: gamma.putWall?.toFixed(2) || '—', c: 'var(--red)' },
              { l: 'P/C Ratio', v: gamma.pcRatio?.toFixed(2) || '—', c: (gamma.pcRatio || 1) > 1.2 ? 'var(--red)' : (gamma.pcRatio || 1) < 0.8 ? 'var(--green)' : 'var(--t1)' },
            ].map(({ l, v, c }) => (
              <div key={l} className="card-inner">
                <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', marginBottom: 2 }}>{l}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: c }}>{v}</div>
              </div>
            ))}
          </div>
          {gamma.expectedMove && (
            <div className="card-inner" style={{ borderLeft: '2px solid var(--blue)', marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--t3)' }}>Estimated 1-day move (from IV)</div>
              <div className="mono" style={{ fontSize: 14, color: 'var(--blue)', fontWeight: 600 }}>±{gamma.expectedMove.toFixed(2)}</div>
            </div>
          )}
          <OIBarChart options={data!.options!} curPrice={data?.candles?.length ? data.candles[data.candles.length - 1].close : undefined} gammaFlip={gamma.gammaFlip} />
          <div className="disclaimer mt8">Gamma and dealer position inferences are based on OI structure only. Not exact dealer positioning data.</div>
        </>
      )}
    </div>
  );
}
